import { subscribeWeeklyCache, weeklyCacheGeneration } from "./projectWeeklyCache.js";

const entries = new Map();
const pending = new Map();
const identityListeners = new Set();
let identity = "unbound";
let currentWeek = null;
let epoch = 0;
let lastRefresh = -Infinity;
const TTL = 30_000;
export function clearWeeklyReads() {
  epoch++;
  entries.clear();
  pending.clear();
  currentWeek = null;
}
subscribeWeeklyCache(clearWeeklyReads);
export function refreshWeeklyReads() {
  // Multiple mounted readers receive the same focus event.
  if (performance.now() - lastRefresh < 100) return;
  lastRefresh = performance.now();
  clearWeeklyReads();
}

// Set before passive data effects run; includes role preview and DEV Person.
export function setWeeklyReadIdentity(next) {
  if (identity === next) return;
  identity = next;
  clearWeeklyReads();
  for (const listener of identityListeners) listener();
}
export const weeklyReadIdentity = () => identity;
export function subscribeWeeklyReadIdentity(listener) {
  identityListeners.add(listener);
  return () => identityListeners.delete(listener);
}

function currentStillValid() {
  return currentWeek && Date.now() < Date.parse(`${currentWeek}T00:00:00Z`) + 7 * 86400000;
}

export function sharedWeeklyRead(shape, week, loader, { force = false, signal } = {}) {
  if (force) clearWeeklyReads();
  const resolvedWeek = week || (currentStillValid() ? currentWeek : "current");
  const key = JSON.stringify([identity, shape, resolvedWeek]);
  const currentKey = JSON.stringify([identity, shape, "current"]);
  if (!force && resolvedWeek === currentWeek && pending.has(currentKey)) {
    return subscribeRead(pending.get(currentKey).then(data => {
      if (week && data.weekDate && data.weekDate !== week) return sharedWeeklyRead(shape, week, loader, { signal });
      return data;
    }), signal);
  }
  const cached = entries.get(key);
  let promise;
  if (cached && Date.now() < cached.expires) promise = Promise.resolve(cached.data);
  else if (pending.has(key)) promise = pending.get(key);
  else {
    const started = epoch;
    const generation = weeklyCacheGeneration();
    promise = Promise.resolve().then(loader).then(data => {
      if (started !== epoch || generation !== weeklyCacheGeneration() || pending.get(key) !== promise) {
        throw new Error("Updates changed while loading. Please retry.");
      }
      if (data?.currentWeek) currentWeek = data.currentWeek;
      // Historical snapshots have a finite revalidation TTL, not their old
      // week-end cutoff. Only aliases meaning "current" expire at rollover.
      let expires = Date.now() + TTL;
      if (!week || data?.weekDate === data?.currentWeek) {
        expires = Math.min(expires, Date.parse(`${data.currentWeek}T00:00:00Z`) + 7 * 86400000);
      }
      const entry = { data, expires };
      entries.set(key, entry);
      if (data?.weekDate) entries.set(JSON.stringify([identity, shape, data.weekDate]), entry);
      else if (shape === "weeks" && data?.currentWeek) entries.set(JSON.stringify([identity, shape, data.currentWeek]), entry);
      for (const [storedKey, value] of entries) {
        if (Date.now() >= value.expires) entries.delete(storedKey);
      }
      while (entries.size > 128) entries.delete(entries.keys().next().value);
      return data;
    }).finally(() => { if (pending.get(key) === promise) pending.delete(key); });
    pending.set(key, promise);
  }
  return subscribeRead(promise, signal);
}

function subscribeRead(promise, signal) {
  // Subscribers can abandon a shared read without cancelling other consumers.
  if (!signal) return promise;
  return new Promise((resolve, reject) => {
    const abort = () => reject(new DOMException("Aborted", "AbortError"));
    if (signal.aborted) abort();
    else signal.addEventListener("abort", abort, { once: true });
    promise.then(value => { signal.removeEventListener("abort", abort); resolve(value); },
      error => { signal.removeEventListener("abort", abort); reject(error); });
  });
}
